/**
 * InheritedPill — small chip stamped next to a field whose value is
 * inherited from a higher level (company defaults, a parent record,
 * a GSTN lookup). Tells the user "this isn't blank, here's where it
 * comes from" plus an optional link to customize.
 *
 * Used by:
 *   · Customer detail cover — "Net 15 terms · company default · change"
 *   · Customer detail credit-limit side card — header chip
 *   · CompletionStrip — `is-todo` rows tagged with their fallback
 *
 * Tone defaults to `muted` (the usual case). Use `verified` (green
 * leading check) for fields auto-derived from a trusted external
 * source like GSTN — the visual cue is "trust this value, it came
 * from outside the app."
 */
import { Link } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { cn } from "@flint/ui";

export type InheritedPillTone = "muted" | "verified";

export interface InheritedPillProps {
  /** Body of the pill, e.g. `"company default"` or `"from GSTN"`. */
  label: string;
  /**
   * Optional href turning the trailing word into a link. Pair with
   * `actionLabel` to override the default `"change"` text.
   */
  settingsHref?: string;
  /** Text for the trailing link. Default `"change"`. */
  actionLabel?: string;
  /** Default `"muted"` — neutral grey. `"verified"` paints a green tick. */
  tone?: InheritedPillTone;
  className?: string;
}

export function InheritedPill({
  label,
  settingsHref,
  actionLabel = "change",
  tone = "muted",
  className,
}: InheritedPillProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2 py-[2px] rounded-full",
        "bg-raised text-muted text-xs font-semibold leading-none tracking-tight",
        className,
      )}
    >
      {tone === "verified" ? (
        <Check aria-hidden className="w-2.5 h-2.5 text-ok" strokeWidth={3} />
      ) : null}
      <span>{label}</span>
      {settingsHref ? (
        <>
          <span aria-hidden className="opacity-60">
            ·
          </span>
          <Link to={settingsHref} className="text-sky-700 font-semibold hover:underline">
            {actionLabel}
          </Link>
        </>
      ) : null}
    </span>
  );
}
